import React from 'react';
import { connect } from 'react-redux';
import Card from 'react-bootstrap/Card';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import {
  emailResponseListFetch,
  emailResponseMarkActioned,
  emailResponseListSetPage,
  emailResponseListSetFilters,
  emailResponseListClearFilters,
} from '../../lib/redux/actions/emailResponses';
import EmailResponseTableFilterForm from '../../components/forms/EmailResponse/EmailResponseTableFilterForm';
import Collapse from 'react-bootstrap/Collapse';
import Alert from 'react-bootstrap/Alert';
import { addToast } from '../../lib/redux/actions/toast';
import { niceDateTimeFormatter } from '../../lib/helper/formatting';
import { requests } from '../../lib/helper/agent';
import * as API_ROUTES from '../../lib/routing/api';
import * as ROUTES from '../../lib/routing/frontend';
import { Link } from 'react-router-dom';
import Paginator from '../../components/common/Paginator';
import MASpinner from '../../components/common/MASpinner';
import EmailAttachments from '../../components/common/EmailAttachments';

const mapStateToProps = (state) => ({
  ...state.emailResponseList,
});

const mapDispatchToProps = {
  emailResponseListFetch,
  emailResponseMarkActioned,
  emailResponseListSetPage,
  emailResponseListSetFilters,
  emailResponseListClearFilters,
  addToast,
};

class EmailResponseListContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      actionedThisSession: [],
      showModal: false,
      selectedResponse: null,
      loadingResponse: false,
      showFilters: false,
    };
  }

  componentDidMount() {
    this.loadEmailResponses();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.page !== this.props.match.params.page) {
      this.loadEmailResponses();
    }
    if (prevProps.filters !== this.props.filters) {
      this.loadEmailResponses();
    }
  }

  getQueryParamPage() {
    return Number(this.props.match.params.page) || 1;
  }

  getCombinedFilters() {
    const { filters } = this.props;
    // Default to showing unactioned responses
    const defaultFilters = { isActioned: false };
    return { ...defaultFilters, ...filters };
  }

  loadEmailResponses() {
    const combinedFilters = this.getCombinedFilters();
    this.props.emailResponseListFetch(this.getQueryParamPage(), {
      ...combinedFilters,
      pagination: true,
    });
  }

  toggleFilters = () => {
    this.setState((prevState) => ({ showFilters: !prevState.showFilters }));
  };

  changePage(page) {
    const { history } = this.props;
    history.push(ROUTES.EMAIL_RESPONSES.ALL.replace(':page?', page));
  }

  nextPage() {
    const { currentPage, pageCount } = this.props;
    const newPage = Math.min(currentPage + 1, pageCount);
    this.changePage(newPage);
  }

  prevPage() {
    const { currentPage } = this.props;
    const newPage = Math.max(currentPage - 1, 1);
    this.changePage(newPage);
  }

  markResponseAsActioned(responseId) {
    const { emailResponseMarkActioned, addToast } = this.props;
    return emailResponseMarkActioned(responseId, '').then(() => {
      this.setState({ actionedThisSession: [...this.state.actionedThisSession, responseId], showModal: false });
      addToast('Email response ' + responseId + ' marked as actioned', 'success');
    });
  }

  viewResponse(responseId) {
    this.setState({ loadingResponse: true, showModal: true });
    requests
      .get(API_ROUTES.EMAIL_RESPONSES + '/' + responseId)
      .then((response) => {
        this.setState({ selectedResponse: response, loadingResponse: false });
      })
      .catch((error) => {
        this.props.addToast('Failed to load email response', 'error');
        this.setState({ loadingResponse: false, showModal: false });
      });
  }

  closeModal = () => {
    this.setState({ showModal: false, selectedResponse: null });
  };

  render() {
    const { emailResponses, isFetchingEmailResponses, pageTitle, currentPage, pageCount, filters } = this.props;
    const showingActioned = filters && filters.isActioned === true;

    return (
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between">
            {pageTitle}

            {!isFetchingEmailResponses && (
              <div>
                <button type="button" className="btn btn-sm btn-outline-secondary mr-3" onClick={this.toggleFilters}>
                  Filters
                </button>
                {emailResponses && emailResponses.length > 0 && (
                  <span className="tabular-data-result-count">{pageCount} pages</span>
                )}
              </div>
            )}
          </div>
        </Card.Header>
        <Collapse in={this.state.showFilters}>
          <div className="p-3 border-bottom">
            <EmailResponseTableFilterForm />
          </div>
        </Collapse>

        {isFetchingEmailResponses && (
          <Card.Body>
            <MASpinner />
          </Card.Body>
        )}

        {!isFetchingEmailResponses && (emailResponses === null || emailResponses.length === 0) && (
          <Card.Body>
            <Alert variant={'info'} className="mb-0">
              No Email Responses found
            </Alert>
          </Card.Body>
        )}

        {!isFetchingEmailResponses && emailResponses && emailResponses.length > 0 && (
          <Card.Body>
            <table className="table">
              <thead>
                <tr>
                  <th>Received At</th>
                  <th>From</th>
                  <th>Subject</th>
                  <th>Hospital</th>
                  <th>Consultant</th>
                  {showingActioned && <th>Actioned At</th>}
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {emailResponses.map((response) => (
                  <tr key={response.id} className={this.state.actionedThisSession.includes(response.id) ? 'bg-info' : ''}>
                    <td>{niceDateTimeFormatter(response.createdAt)}</td>
                    <td>
                      <div>
                        <strong>{response.fromEmail}</strong>
                      </div>
                      {response.fromName && <div className="small text-muted">{response.fromName}</div>}
                    </td>
                    <td>{response.subject}</td>
                    <td>{response.hospitalName || '-'}</td>
                    <td>{response.consultantName || '-'}</td>
                    {showingActioned && (
                      <td>
                        {response.actionedAt ? (
                          <>
                            <div>{niceDateTimeFormatter(response.actionedAt)}</div>
                            {response.actionedByUser && (
                              <div className="small text-muted">
                                by {response.actionedByUser.firstName} {response.actionedByUser.lastName}
                              </div>
                            )}
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                    )}
                    <td>
                      <button className="btn btn-sm btn-secondary mr-2" onClick={() => this.viewResponse(response.id)}>
                        View Response
                      </button>
                      {!response.actionedAt && !this.state.actionedThisSession.includes(response.id) && (
                        <button className="btn btn-sm btn-primary" onClick={() => this.markResponseAsActioned(response.id)}>
                          Mark Actioned
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card.Body>
        )}

        {!isFetchingEmailResponses && emailResponses && emailResponses.length > 0 && (
          <Paginator
            currentPage={currentPage}
            pageCount={pageCount}
            setPage={this.changePage.bind(this)}
            nextPage={this.nextPage.bind(this)}
            prevPage={this.prevPage.bind(this)}
          />
        )}

        <Modal show={this.state.showModal} onHide={this.closeModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Email Response Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {this.state.loadingResponse && <Alert variant="info">Loading email response...</Alert>}
            {!this.state.loadingResponse && this.state.selectedResponse && (
              <div>
                <div className="mb-3">
                  <strong>From:</strong> {this.state.selectedResponse.fromEmail}
                  {this.state.selectedResponse.fromName && <span> ({this.state.selectedResponse.fromName})</span>}
                </div>
                <div className="mb-3">
                  <strong>Subject:</strong> {this.state.selectedResponse.subject}
                </div>
                <div className="mb-3">
                  <strong>Hospital:</strong> {this.state.selectedResponse.hospitalName || '-'}
                </div>
                <div className="mb-3">
                  <strong>Consultant:</strong> {this.state.selectedResponse.consultantName || '-'}
                </div>
                <div className="mb-3">
                  <strong>Received At:</strong> {niceDateTimeFormatter(this.state.selectedResponse.createdAt)}
                </div>
                {this.state.selectedResponse.invoiceNotificationEvent && (
                  <div className="mb-3">
                    <strong>Original Notification Event:</strong> <span>Event ID {this.state.selectedResponse.invoiceNotificationEvent.id}</span>
                  </div>
                )}
                {((this.state.selectedResponse.invoiceNotificationEvent?.relatedBatchInvoices &&
                  this.state.selectedResponse.invoiceNotificationEvent.relatedBatchInvoices.length > 0) ||
                  (this.state.selectedResponse.invoiceNotificationEvent?.relatedInvoiceData &&
                    this.state.selectedResponse.invoiceNotificationEvent.relatedInvoiceData.length > 0) ||
                  (this.state.selectedResponse.invoiceIds && this.state.selectedResponse.invoiceIds.length > 0)) && (
                  <div className="mb-3">
                    <strong>Related Invoices:</strong>
                    <div className="mt-1">
                      {/* Display batch invoices (hospital batch chase emails - hb- prefix) */}
                      {this.state.selectedResponse.invoiceNotificationEvent?.relatedBatchInvoices?.map((invoice, index) => (
                        <div key={`batch-${index}`} className="mb-1">
                          <Link
                            to={ROUTES.INVOICES.SINGLE.replace(':id', invoice.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary mr-2">
                            Invoice {invoice.invoiceNo}
                          </Link>
                          {/*<span className="text-muted small">*/}
                          {/*  {invoice.patientName}*/}
                          {/*</span>*/}
                        </div>
                      ))}
                      {/* Display individual invoice data (hi-/hc-/hs- prefix emails) */}
                      {this.state.selectedResponse.invoiceNotificationEvent?.relatedInvoiceData?.map((invoice, index) => (
                        <div key={`individual-${index}`} className="mb-1">
                          <Link
                            to={ROUTES.INVOICES.SINGLE.replace(':id', invoice.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary mr-2">
                            Invoice {invoice.invoiceNo}
                          </Link>
                          {/*<span className="text-muted small">*/}
                          {/*  {invoice.patientName}*/}
                          {/*</span>*/}
                        </div>
                      ))}
                      {/* Fallback: Display invoiceIds if no detailed data available (legacy) */}
                      {!this.state.selectedResponse.invoiceNotificationEvent?.relatedBatchInvoices?.length &&
                        !this.state.selectedResponse.invoiceNotificationEvent?.relatedInvoiceData?.length &&
                        this.state.selectedResponse.invoiceIds?.map((invoiceId) => (
                          <div key={`invoice-${invoiceId}`} className="mb-1">
                            <Link
                              to={ROUTES.INVOICES.SINGLE.replace(':id', invoiceId)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary mr-2">
                              Invoice {invoiceId}
                            </Link>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                <div className="mb-3">
                  <strong>Email Content:</strong>
                  <div className="mt-2 p-3 border bg-light" style={{ whiteSpace: 'pre-wrap', maxHeight: '400px', overflowY: 'auto' }}>
                    {this.state.selectedResponse.content}
                  </div>
                </div>
                <EmailAttachments attachableType="email_response" attachableId={this.state.selectedResponse.id} />
                {this.state.selectedResponse.actionedAt && (
                  <div className="mb-3">
                    <Alert variant="success">
                      This response has been marked as actioned
                      {this.state.selectedResponse.actionedAt && <> on {niceDateTimeFormatter(this.state.selectedResponse.actionedAt)}</>}
                      {this.state.selectedResponse.actionedByUser && (
                        <>
                          {' '}
                          by {this.state.selectedResponse.actionedByUser.firstName} {this.state.selectedResponse.actionedByUser.lastName}
                        </>
                      )}
                    </Alert>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            {!this.state.selectedResponse?.actionedAt && !this.state.actionedThisSession.includes(this.state.selectedResponse?.id) && (
              <Button variant="primary" onClick={() => this.markResponseAsActioned(this.state.selectedResponse.id)}>
                Mark as Actioned
              </Button>
            )}
            <Button variant="secondary" onClick={this.closeModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Card>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(EmailResponseListContainer);
